import {
    container,
    type IGetImpresaLogin,
    type ILoginImpresa,
    Symbols,
} from "@dav/core";
import {
    type ActionFunctionArgs,
    Form,
    Link,
    type LoaderFunctionArgs,
    type MetaFunction,
    redirect,
    useActionData,
    useNavigation,
} from "react-router";
import { z } from "zod";
import { impresa_cookie } from "~/cookies.server";
import getImpresaSessId from "~/shared/utils/getImpresaSessId";

const validator = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});

type ActionData = {
    email?: string;
    password?: string;
    form?: string;
    values?: { email: string };
};

export const meta: MetaFunction = () => [{ title: "Login Impresa" }];

export async function loader({ request }: LoaderFunctionArgs) {
    const sessionId = await getImpresaSessId(request);
    if (!sessionId.isSome()) {
        return null;
    }

    const impresaLogin = await container
        .get<IGetImpresaLogin>(Symbols.UseCase.impresa_login.GetImpresaLogin)
        .execute({ impresa_session_id: sessionId.data });

    if (impresaLogin.isSuccess()) {
        return redirect("/impresa");
    }

    return new Response(null, {
        headers: {
            "Set-Cookie": await impresa_cookie.serialize("", { maxAge: -1 }),
        },
    });
}

export async function action({ request }: ActionFunctionArgs) {
    const formData = await request.formData();
    const raw = Object.fromEntries(formData);
    const parsed = validator.safeParse(raw);

    if (!parsed.success) {
        const { fieldErrors } = parsed.error.flatten();
        return {
            email: fieldErrors.email?.join(", ") ?? undefined,
            password: fieldErrors.password?.join(", ") ?? undefined,
            form: "Controlla i dati inseriti",
            values: { email: String(raw.email ?? "") },
        } satisfies ActionData;
    }

    const result = await container
        .get<ILoginImpresa>(Symbols.UseCase.impresa_login.LoginImpresa)
        .execute(parsed.data);

    if (!result.isSuccess()) {
        return {
            form: result.error.message,
            values: { email: parsed.data.email },
        } satisfies ActionData;
    }

    const login = result.data;

    return redirect("/impresa", {
        headers: {
            "Set-Cookie": await impresa_cookie.serialize(login._id, {
                expires: new Date(login.expires_at),
            }),
        },
    });
}

export default function LoginRoute() {
    const actionData = useActionData<typeof action>() as ActionData | undefined;
    const navigation = useNavigation();
    const isSubmitting = navigation.state === "submitting";

    return (
        <div className="hero bg-base-200 min-h-screen pt-safe">
            <img
                src="/icona-full.svg"
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-center opacity-10 pointer-events-none"
            />
            <div className="hero-content flex-col lg:flex-row-reverse gap-16">
                <div className="text-center lg:text-left max-w-xl">
                    <h1 className="text-5xl font-bold text-base-content">
                        Portale Imprese DAV
                    </h1>
                    <p className="pt-6 text-base-content/70 text-lg">
                        Accedi con le credenziali fornite dall'amministratore
                        per gestire i tuoi servizi e visualizzare le
                        informazioni aggiornate della tua impresa.
                    </p>
                    <p className="py-6 text-base-content/70 text-lg">
                        Non hai ancora un account? <Link to="/pre-registrazione" className="text-primary">Registrati.</Link>
                    </p>
                </div>

                <div className="card bg-base-100 w-full max-w-sm shrink-0 shadow-2xl">
                    <Form method="post" className="card-body space-y-2">
                        <h2 className="text-2xl font-semibold text-base-content">
                            Accedi al tuo account
                        </h2>

                        <label className="input input-bordered flex items-center gap-2">
                            <span className="text-sm">Email</span>
                            <input
                                name="email"
                                type="email"
                                className="grow"
                                placeholder="esempio@impresa.it"
                                defaultValue={actionData?.values?.email ?? ""}
                                autoComplete="email"
                                required
                            />
                        </label>
                        <span className="text-sm text-error">
                            {actionData?.email}
                        </span>

                        <label className="input input-bordered flex items-center gap-2">
                            <span className="text-sm">Password</span>
                            <input
                                name="password"
                                type="password"
                                className="grow"
                                placeholder="••••••"
                                autoComplete="current-password"
                                required
                            />
                        </label>
                        <span className="text-sm text-error">
                            {actionData?.password}
                        </span>

                        <span className="text-sm text-error">
                            {actionData?.form}
                        </span>

                        <div className="form-control mt-4">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting && (
                                    <span className="loading loading-spinner loading-sm" />
                                )}
                                {isSubmitting
                                    ? "Accesso in corso..."
                                    : "Accedi"}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
}
